/**
 * Funil e canais no modo ao vivo: etapas medidas de verdade (Meta, VTurb, Kiwify), vendas por canal e
 * as vendas sem origem (Rastreamento).
 *
 * Nada do funil simulado passa como real: etapa sem fonte fica vazia (com o motivo), a tabela de
 * canais sai dos pedidos da Kiwify, as consultorias de R$ 100 mil (ainda não registradas) ficam fora e
 * a cascata do CPA (V1) volta vazia. Ramos do orgânico, com e sem quiz, perfil do quiz e saúde da
 * medição saem dos pedidos reais ou vazios (`liveCohort`), nunca da simulação. Venda de anúncio conta
 * pela Meta (Gerenciador); o canal da Kiwify pelo link (UTM) é só referência, com o rótulo na tela.
 *
 * Rastreamento e quiz (o tracker próprio ainda não está instalado): o semáforo e os KPIs dele saem
 * dos pedidos reais e das compras da Meta (`liveTrackingHealth`); o log de eventos, dos pedidos da
 * Kiwify (`liveEvents`); KPIs do quiz e do funil próprio, relatório do quiz, segmentos, séries por hora
 * do FUN-04, diagnóstico de UTMs e links do redirecionador ficam sem valor ou vazios, com o motivo.
 */
import type {
  ChannelReport,
  ChannelRow,
  ColumnarSeries,
  Device,
  Freshness,
  FunnelEventType,
  FunnelReport,
  FunnelStageIdV2,
  FunnelStageV2,
  FunnelStep,
  FunnelStepId,
  FunnelV2Report,
  KpiId,
  KpiIdV2,
  KpiStatus,
  KpiValue,
  ListQuery,
  MeasurementEventV2,
  OrganicBranchId,
  Page,
  PainelProviderV2,
  Placement,
  Query,
  QuizReport,
  SegmentReport,
  SeriesQuery,
  SuiteFreshness,
  TrackedEventRow,
  TrackedLink,
  TrackingHealth,
  UpsellBuyerOriginV2,
} from '@rota/contracts';
import { channelGroup } from '@/lib/salesChannel';
import {
  buckets,
  channelV2,
  DAY,
  HOUR,
  isPaid,
  isRefundLike,
  isSold,
  kv,
  matches,
  noRealKpi,
  orderDates,
  paginate,
  type RealCtx,
  type RealKpiInput,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  revenueOf,
  todayKey,
} from './realCore';
import type {
  LiveChannelRow,
  LiveFunnelExtras,
  LiveFunnelV2Report,
  NotMeasuredReason,
} from './realFunnelTypes';
import { mediaMissing, metaFreshness, type RealMedia } from './realMedia';
import { reportsModule } from './realReports';
import { type RealVsl, vslStageCounts } from './realVsl';

type GetFunnelV2 = NonNullable<PainelProviderV2['getFunnelV2']>;

export {
  type LiveChannelRow,
  type LiveFunnelExtras,
  liveExtras,
  type NotMeasuredReason,
} from './realFunnelTypes';

/**
 * Canal da venda pela regra única (`@/lib/salesChannel`, a mesma do Hoje e da tela Vendas): anúncio é
 * `meta_ads`; orgânico, tudo o que tem origem e não é anúncio (bio, automação/direct, outros links).
 * Sem origem nunca é presumida orgânica.
 */
export function inChannelView(o: RealOrderRow, view: 'all' | 'ads' | 'organic'): boolean {
  if (view === 'all') return true;
  return channelGroup(o.channel) === (view === 'ads' ? 'ads' : 'org');
}

/** Motivo padrão de cada etapa sem número. */
function reasonOf(
  id: FunnelStageIdV2,
  view: 'all' | 'ads' | 'organic',
  v: RealVsl | null,
): NotMeasuredReason {
  switch (id) {
    case 'impressions':
    case 'clicks':
      return view === 'organic' ? 'organic_no_media' : 'meta_off';
    case 'vsl_visit':
    case 'vsl_play':
    case 'minute1':
    case 'pitch':
      // VTurb ligada que não respondeu: "não respondeu", não "não conectada".
      if (v?.configured && v.error) return 'vturb_error';
      return v?.configured && v.main && view !== 'all' ? 'vturb_channel' : 'vturb_off';
    case 'cta_click':
      return 'cta_event';
    case 'checkout':
      return 'checkout_event';
    case 'b1_contact':
    case 'b5_proposal':
    case 'b7_won':
      return 'manual';
    default:
      // Quiz e qualquer etapa nova sem fonte real: o tracker próprio ainda não está instalado.
      return 'no_tracker';
  }
}

/**
 * Tabela de canais com os pedidos reais. "Receita líquida" = valor dos produtos − taxa da Kiwify (o
 * que fica na empresa; a comissão de coprodução não é descontada). Cliques só no anúncio (Meta);
 * quiz, plays e consultorias por canal não são medidos.
 */
export function liveChannelRows(
  paid: readonly RealOrderRow[],
  m: RealMedia | null,
): { rows: LiveChannelRow[]; coverage: number } {
  const order = ['meta_ads', 'ig_bio', 'automacao', 'outro', 'sem_origem'] as const;
  const allFront = paid.filter((o) => o.kind === 'front').length;
  const rows = order.map((ch): LiveChannelRow => {
    const mine = paid.filter((o) => o.channel === ch);
    const front = mine.filter((o) => o.kind === 'front').length;
    const keep = mine.reduce((s, o) => s + revenueOf(o) - (o.feeCents ?? 0), 0);
    return {
      channel: channelV2[ch],
      clicks: ch === 'meta_ads' && m ? m.totals.linkClicks : null,
      quizCompletes: null,
      vslPlays: null,
      frontSales: front,
      upsellSales: mine.filter((o) => o.kind === 'upsell').length,
      backendClosed: null,
      revenueNet: keep,
      backendGross: 0,
      // Sem a leitura real da Meta, o gasto dos Anúncios fica sem valor (nunca R$ 0); os outros, 0.
      spend: ch === 'meta_ads' ? (m ? m.totals.spendCents : null) : 0,
      ltvPerBuyer: front > 0 ? Math.round(keep / front) : null,
      share: allFront > 0 ? front / allFront : 0,
    };
  });
  const none = rows.find((r) => r.channel === 'sem_origem')?.frontSales ?? 0;
  return { rows, coverage: allFront > 0 ? 1 - none / allFront : 1 };
}

/**
 * Medição ao vivo: a do contrato com o clique do anúncio (fbclid) que chegou na venda. O clique no
 * botão de compra (`salesWithClick`) não é medido sem o tracker: `ctaTracked` fica falso e a tela
 * diz "não medido", nunca "0 de N".
 */
export type LiveMeasurement = FunnelV2Report['measurement'] & {
  live: true;
  /** Vendas do curso com o clique da Meta (cookie _fbc no sck); ausente em servidor antigo. */
  adClick?: { n: number; of: number };
};

type Cohort = Pick<FunnelV2Report, 'branches' | 'withWithoutQuiz' | 'quizProfile'> & {
  measurement: LiveMeasurement;
};

/** Os quatro ramos do orgânico (a porta do orgânico pede sempre os quatro). */
const BRANCHES: readonly OrganicBranchId[] = ['bio_quiz', 'bio_vsl', 'dm_quiz', 'story_vsl'];
/** Mínimo de visitas por lado para comparar com e sem quiz (o mesmo da demonstração). */
const MIN_VISITS = 300;

/** Origem do comprador do 20X pelo canal da venda na Kiwify (anúncio pelo link/UTM). */
const ORIGIN_LABEL: Record<RealOrderRow['channel'], string> = {
  meta_ads: 'Anúncios (pelo link)',
  ig_bio: 'Orgânico · bio',
  automacao: 'Orgânico · direct (automação)',
  outro: 'Orgânico · outro link',
  sem_origem: 'Sem origem',
};

/**
 * Compradores do 20X reais (sem dado pessoal): origem pelo canal da venda; o código de perfil, o ramo
 * do quiz e a consultoria ainda não são medidos (null, nunca "sem quiz" nem um deal inventado).
 */
export function liveUpsellBuyers(paid: readonly RealOrderRow[]): UpsellBuyerOriginV2[] {
  return paid
    .filter((o) => o.kind === 'upsell')
    .sort((a, b) => a.at - b.at)
    .map((o) => ({
      orderId: o.id,
      at: o.at,
      channel: channelV2[o.channel],
      branch: null,
      originLabel: ORIGIN_LABEL[o.channel],
      quizProfile: null,
      profileSummary: null,
      moment: null,
      backend: null,
    }));
}

/**
 * Porta do orgânico, com e sem quiz, perfil do quiz e saúde da medição ao vivo. O quiz, o
 * redirecionador da bio e o clique do botão ainda não têm tracker instalado: ramos zerados (QUI-06 e
 * QUI-07 ficam vazios), perfil sem linhas por momento e nenhum evento do tracker como medido. Só sai
 * dos pedidos reais o que eles mostram: compradores do curso e do 20X (com a origem pelo canal),
 * vendas de anúncio pelo link com `utm_content`, o `sck` e o clique da Meta (fbclid) no checkout.
 * Nada da simulação passa como real.
 */
export function liveCohort(paid: readonly RealOrderRow[], fallbackSince: string): Cohort {
  const front = paid.filter((o) => o.kind === 'front');
  const first = front.reduce<number | null>((a, o) => (a == null || o.at < a ? o.at : a), null);
  const since = first != null ? todayKey(first) : fallbackSince;
  // Venda de anúncio pelo link (UTM): é a qualidade do rastreio da Kiwify, não a contagem da Meta.
  const ads = front.filter((o) => o.channel === 'meta_ads');
  const adsUtm = ads.filter((o) => (o.utmContent ?? '').trim() !== '').length;
  const cov = (n: number, of: number): MeasurementEventV2['status'] =>
    of === 0 ? 'missing' : n / of >= 0.95 ? 'ok' : n > 0 ? 'partial' : 'missing';
  // Servidor antigo não manda o `sck` nem o clique: o item sai da lista em vez de aparecer como "falta".
  const hasSck = front.some((o) => o.sck !== undefined);
  const withSck = front.filter((o) => (o.sck ?? '').trim() !== '').length;
  const hasClick = front.some((o) => o.clickTracked !== undefined);
  // CAPI: só conta quando o servidor informa (true/false); null = tabela dos eventos inexistente.
  const capiOf = paid.some((o) => o.capiSent != null) ? paid : [];
  const capiSent = capiOf.filter((o) => o.capiSent === true).length;
  const events: MeasurementEventV2[] = [
    // Botão e oferta liberada são eventos do tracker próprio, que ainda não está instalado: "falta",
    // sem contagem (um "0 de N" daria a entender que foi medido e deu zero).
    { id: 'initiate_checkout', status: 'missing' },
    { id: 'offer_unlocked', status: 'missing' },
    // Sem venda de anúncio pelo link no período (dia só orgânico, filtro do orgânico): nada a medir.
    // O item sai da lista em vez de aparecer como "Falta" com "0 de 0" (não é falha do rastreio).
    ...(ads.length > 0
      ? [{ id: 'utm_ads' as const, status: cov(adsUtm, ads.length), coverage: { n: adsUtm, of: ads.length } }]
      : []),
    ...(hasSck
      ? [
          {
            id: 'sck_checkout' as const,
            status: cov(withSck, front.length),
            coverage: { n: withSck, of: front.length },
          },
        ]
      : []),
    // Compra enviada à Meta pela API de Conversões (Purchase com valor), sobre todos os pedidos pagos do
    // período. Servidor antigo ou CAPI nunca ligada: o item sai em vez de mostrar "0 de N".
    ...(capiOf.length > 0
      ? [
          {
            id: 'purchase_value' as const,
            status: cov(capiSent, capiOf.length),
            coverage: { n: capiSent, of: capiOf.length },
          },
        ]
      : []),
    // Nenhum pedido chega com o código de perfil do quiz (o quiz ainda não manda o código ao checkout).
    { id: 'quiz_profile', status: 'missing' },
  ];
  const none = { visits: 0, sales: 0, conv: null };
  return {
    branches: BRANCHES.map((id) => ({
      id,
      clicks: 0,
      clickShare: 0,
      quizCompletes: null,
      vslVisits: 0,
      frontSales: 0,
      upsellSales: 0,
      backendWon: 0,
      backendNegotiating: 0,
    })),
    withWithoutQuiz: {
      withQuiz: none,
      withoutQuiz: none,
      diff: null,
      pValue: null,
      verdict: 'insufficient',
      minVisits: MIN_VISITS,
    },
    quizProfile: {
      since,
      salesViaQuiz: 0,
      withCode: 0,
      coverage: null,
      rows: [],
      upsellBuyers: liveUpsellBuyers(paid),
    },
    measurement: {
      live: true,
      since,
      ctaTracked: false,
      ctaClicks: 0,
      frontSales: front.length,
      salesWithClick: 0,
      metaSales: ads.length,
      metaWithUtmContent: adsUtm,
      events,
      ...(hasClick
        ? { adClick: { n: front.filter((o) => o.clickTracked === true).length, of: front.length } }
        : {}),
    },
  };
}

/** Etapas da V1 que têm fonte real (as outras saem da lista, nunca com número inventado). */
const V1_STEPS: readonly {
  id: FunnelStepId;
  label: string;
  source: FunnelStep['source'];
  rateKpi?: KpiId;
}[] = [
  { id: 'impressions', label: 'Impressões', source: 'meta' },
  { id: 'linkClicks', label: 'Cliques no link', source: 'meta', rateKpi: 'ctrLink' },
  { id: 'lpv', label: 'Chegaram na página', source: 'meta', rateKpi: 'connectRate' },
  { id: 'vslView', label: 'Viram a VSL', source: 'vturb' },
  { id: 'vslPlay', label: 'Deram play', source: 'vturb', rateKpi: 'vslPlayRate' },
  { id: 'pitch', label: 'Chegaram na oferta', source: 'vturb', rateKpi: 'pitchRetention' },
  { id: 'frontSale', label: 'Compraram o curso', source: 'kiwify' },
  { id: 'upsellSale', label: 'Compraram o 20X', source: 'kiwify', rateKpi: 'upsellTakeRate' },
];

const DEVICES: readonly Device[] = ['mobile', 'desktop', 'tablet'];
const PLACEMENTS: readonly Placement[] = ['feed', 'stories', 'reels', 'other'];

// ---------- Rastreamento, quiz e eventos (sem tracker próprio instalado) ----------

/** Motivos de "não medido" (nota do KPI; a tela mostra "Sem dado real ainda" + o motivo). */
const NO_TRACKER = 'não medido: o tracker próprio (quiz, aula e botão de compra) ainda não está instalado';
const NO_VTURB = 'não medido: a VTurb não está conectada';
const NO_REDIRECTOR = 'não medido: o redirecionador de links ainda não existe';
const NO_WEBHOOK_LAG = 'não medido: o servidor ainda não informa a hora em que cada aviso da Kiwify chegou';
/** Mínimo de vendas pagas com o atraso medido para o p95 valer. */
const WEBHOOK_LAG_MIN = 20;
/** A hora da venda vem da Kiwify sem os segundos: o atraso medido pode ser até 60 s maior que o real. */
const SALE_MINUTE_SEC = 60;

/**
 * Atraso do aviso da Kiwify (p95, em segundos): chegada no servidor − hora da venda, só nas vendas pagas
 * cujo 1º aviso foi a própria aprovação (Pix e boleto chegam antes, no "gerado") e feitas depois do
 * início do webhook (a exportação não é aviso). A hora da venda vem sem os segundos, então o número é o
 * teto: o atraso real fica entre (p95 − 60 s) e o p95. O status só afirma o que essa folga permite.
 */
export function webhookLagP95(
  paid: readonly RealOrderRow[],
  webhookSince: number | null,
): { p95: number | null; n: number; known: boolean } {
  const known = paid.some((o) => o.receivedAt !== undefined);
  const lags = paid
    .filter((o) => o.receivedAt != null && (webhookSince == null || o.at >= webhookSince))
    .map((o) => ((o.receivedAt as number) - (o.approvedAt ?? o.at)) / 1000)
    .filter((x) => x >= 0 && x < 6 * 3600)
    .sort((a, b) => a - b);
  if (lags.length === 0) return { p95: null, n: 0, known };
  const p95 = lags[Math.min(lags.length - 1, Math.ceil(lags.length * 0.95) - 1)] as number;
  return { p95: Math.round(p95), n: lags.length, known };
}

/** Status do atraso com a folga do minuto: só "bom" ou "ruim" quando a folga inteira cai na faixa. */
function lagStatus(p95: number): KpiStatus {
  const low = Math.max(0, p95 - SALE_MINUTE_SEC);
  if (p95 <= BANDS.webhookLagP95Sec.good) return 'good';
  if (low > BANDS.webhookLagP95Sec.warn) return 'bad';
  if (low > BANDS.webhookLagP95Sec.good) return 'warn';
  return 'insufficient';
}
const NO_SCK = 'não medido: o servidor ainda não manda o sck dos pedidos';

/**
 * KPIs que dependem só do tracker próprio (quiz, visita da aula, botão, checkout iniciado): sem ele,
 * ficam sem valor com o motivo, nunca com o número da simulação (QUI-01, FUN-04, FUN-01, detalhe ?m=).
 */
export const TRACKER_KPIS = [
  'connectReal',
  'quizViews',
  'quizStartRate',
  'quizCompletionRate',
  'quizMedianTimeSec',
  'quizToVsl',
  'postPitchCtr',
  'initiateCheckouts',
  'icRate',
  'cpi',
  'checkoutConv',
  'rpv',
  'cartAbandonRate',
] as const satisfies readonly KpiId[];

/** Taxas da VSL do FUN-04: reais só com a VTurb ligada (o módulo da VSL), senão "não medido". */
const VSL_RATE_KPIS = ['vslPlayRate', 'pitchRetention'] as const satisfies readonly KpiId[];

/** Indicadores do semáforo (TRK-01, HOJ-11): dos pedidos reais da Kiwify e das compras da Meta. */
export const TRACKING_KPIS = [
  'utmCoverage',
  'sckCoverage',
  'pixelKiwifyDivergence',
  'webhookLagP95Sec',
  'untrackedSales',
] as const satisfies readonly KpiId[];

/** Motivo dos indicadores do semáforo quando os pedidos da Kiwify não carregam. */
const NO_KIWIFY_ORDERS = 'Kiwify sem resposta: os pedidos não carregaram';

type KpiText = { label: string; short?: string; help: string; formula: string };

/**
 * Nome, ajuda e fórmula dos indicadores do semáforo no modo ao vivo (`kpiDefs` do boot): a conta é a
 * dos pedidos da Kiwify, não a da simulação ("IDs válidos de campanha, conjunto e anúncio"). O
 * `utm_content` das vendas traz só o nome do anúncio, sem o ID; a UTM conta anúncio e orgânico.
 */
export const LIVE_TRACKING_TEXTS: Readonly<Partial<Record<KpiId, KpiText>>> = {
  utmCoverage: {
    label: 'Vendas com UTM',
    short: 'Com UTM',
    help: 'Vendas pagas (curso, 20X e Premium) que chegaram na Kiwify com UTM no link, de anúncio ou orgânico (bio, story, direct). A UTM dos anúncios traz o nome do anúncio, sem o ID.',
    formula: 'vendas pagas com utm_source ÷ vendas pagas',
  },
  untrackedSales: {
    label: 'Vendas sem origem',
    short: 'Sem origem',
    help: 'Vendas do curso que chegaram na Kiwify sem UTM: não dá para saber de onde vieram. O status é julgado pela fração das vendas do curso.',
    formula: '# vendas do curso sem UTM (status: ÷ vendas do curso)',
  },
};

/** Troca os textos do semáforo pelos do modo ao vivo no boot. */
export function withLiveTrackingTexts<B>(boot: B): B {
  const defs = (boot as { kpiDefs?: Record<string, object> }).kpiDefs;
  if (!defs) return boot;
  const next = { ...defs };
  for (const [id, text] of Object.entries(LIVE_TRACKING_TEXTS)) {
    const d = next[id];
    if (d && text) next[id] = { ...d, ...text };
  }
  return { ...boot, kpiDefs: next };
}

/** KPIs da V2 que só existem com o tracker ou o redirecionador. */
const TRACKER_KPIS_V2 = ['ctaClickCoverage', 'trackedClicks'] as const satisfies readonly KpiIdV2[];

/** Semáforo ao vivo: o do contrato com o motivo de cada indicador sem número. */
export type LiveTrackingHealth = TrackingHealth & {
  live: true;
  /** Por indicador: a conta usada ("79 de 83 vendas pagas com UTM") ou o motivo de não ter número. */
  notes: Partial<Record<KpiId, string>>;
};

/** Faixas do semáforo (as do TRK-01 e do KpiDef). `untrackedSales` vale para a fração do curso. */
const BANDS: Record<
  'utmCoverage' | 'sckCoverage' | 'pixelKiwifyDivergence' | 'untrackedSales' | 'webhookLagP95Sec',
  { good: number; warn: number; higher: boolean }
> = {
  // Atraso do aviso (s): as faixas do TRK-01 (até 10 s bom, até 60 s atenção).
  webhookLagP95Sec: { good: 10, warn: 60, higher: false },
  utmCoverage: { good: 0.9, warn: 0.8, higher: true },
  sckCoverage: { good: 0.85, warn: 0.7, higher: true },
  pixelKiwifyDivergence: { good: 0.15, warn: 0.3, higher: false },
  untrackedSales: { good: 0.05, warn: 0.2, higher: false },
};
/** Amostra mínima da divergência (KpiDef: 20 vendas do curso). */
const DIVERGENCE_MIN = 20;

function bandStatus(x: number | null, b: { good: number; warn: number; higher: boolean }): KpiStatus {
  if (x == null) return 'neutral';
  if (b.higher) return x >= b.good ? 'good' : x >= b.warn ? 'warn' : 'bad';
  return x <= b.good ? 'good' : x <= b.warn ? 'warn' : 'bad';
}

const pct0 = (x: number) => `${Math.round(x * 100)}%`;

/**
 * Semáforo do rastreamento com os pedidos pagos do período (já filtrados) e a Meta:
 * - UTM = pagos com `utm_source` ÷ pagos; sck = pagos com `sck` ÷ pagos;
 * - não trackeadas = vendas do curso sem origem (a mesma regra da lista do TRK-03), sobre o curso;
 * - divergência = |compras que a Meta atribui − vendas do curso na Kiwify| ÷ vendas do curso;
 * - atraso do webhook (p95): chegada do aviso − hora da venda (`webhookLagP95`), com a folga do minuto.
 * A frase e o estado geral saem dessas contas, com as faixas do semáforo.
 */
export function liveTrackingHealth(
  paid: readonly RealOrderRow[],
  m: RealMedia | null,
  metaGap: string | null,
  now: number,
  r: { from: number; to: number },
  fresh: Freshness[],
  webhookSince: number | null = null,
): { health: LiveTrackingHealth; kpis: Partial<Record<KpiId, KpiValue>> } {
  const front = paid.filter((o) => o.kind === 'front');
  const filled = (v: string | null | undefined) => (v ?? '').trim() !== '';
  const kpis: Partial<Record<KpiId, KpiValue>> = {};
  const notes: Partial<Record<KpiId, string>> = {};
  const put = (
    id: KpiId,
    value: number | null,
    status: KpiStatus,
    note: string,
    extra: Partial<KpiValue> = {},
  ) => {
    kpis[id] = { ...kv(id, value, null, now), status, note, freshness: fresh, ...extra };
    notes[id] = note;
  };
  // UTM: qualquer venda paga do período (curso, 20X e Premium).
  const utmN = paid.filter((o) => filled(o.utmSource)).length;
  const utm = paid.length ? utmN / paid.length : null;
  put(
    'utmCoverage',
    utm,
    bandStatus(utm, BANDS.utmCoverage),
    paid.length
      ? `${utmN} de ${paid.length} vendas pagas com UTM (anúncio e orgânico)`
      : 'sem vendas pagas no período',
  );
  // sck: só com o servidor novo (servidor antigo não manda o campo: não medido, nunca "0%").
  const hasSck = paid.some((o) => o.sck !== undefined);
  const sckN = paid.filter((o) => filled(o.sck)).length;
  const sck = hasSck && paid.length ? sckN / paid.length : null;
  put(
    'sckCoverage',
    sck,
    bandStatus(sck, BANDS.sckCoverage),
    !hasSck && paid.length
      ? NO_SCK
      : paid.length
        ? `${sckN} de ${paid.length} vendas pagas com sck`
        : 'sem vendas pagas no período',
  );
  // Não trackeadas: a contagem (o TRK-01 divide pelas vendas do curso, como a lista do TRK-03).
  const noOrigin = front.filter((o) => o.channel === 'sem_origem').length;
  const share = front.length ? noOrigin / front.length : null;
  put(
    'untrackedSales',
    front.length ? noOrigin : null,
    bandStatus(share, BANDS.untrackedSales),
    front.length
      ? `${noOrigin} de ${front.length} vendas do curso sem origem`
      : 'sem vendas do curso no período',
  );
  // Divergência: compras que a Meta atribui aos anúncios × vendas do curso na Kiwify.
  const purchases = !metaGap && m?.meta ? m.meta.purchases : null;
  const div = purchases != null && front.length ? Math.abs(purchases - front.length) / front.length : null;
  const small = div != null && front.length < DIVERGENCE_MIN;
  put(
    'pixelKiwifyDivergence',
    div,
    small ? 'insufficient' : bandStatus(div, BANDS.pixelKiwifyDivergence),
    purchases == null
      ? `não medido: ${metaGap ?? 'a Meta não informou as compras atribuídas'}`
      : `${purchases} compras que a Meta atribui × ${front.length} vendas do curso na Kiwify`,
    {
      sample: { n: front.length, min: DIVERGENCE_MIN, unit: 'vendas' },
      ...(m ? { freshness: [...fresh, ...metaFreshness(now, m, r)] } : {}),
    },
  );
  const lag = webhookLagP95(paid, webhookSince);
  if (!lag.known) {
    kpis.webhookLagP95Sec = noRealKpi('webhookLagP95Sec', NO_WEBHOOK_LAG);
    notes.webhookLagP95Sec = NO_WEBHOOK_LAG;
  } else if (lag.p95 == null) {
    put('webhookLagP95Sec', null, 'neutral', 'sem venda paga com o aviso da aprovação no período');
  } else {
    put(
      'webhookLagP95Sec',
      lag.p95,
      lag.n < WEBHOOK_LAG_MIN ? 'insufficient' : lagStatus(lag.p95),
      `até ${lag.p95} s em 95% das ${lag.n} vendas com o aviso da aprovação (a Kiwify manda a hora da venda sem os segundos: o atraso real pode ser até 60 s menor)`,
      { sample: { n: lag.n, min: WEBHOOK_LAG_MIN, unit: 'vendas' } },
    );
  }

  const indicators = TRACKING_KPIS.map((kpi) => ({
    kpi,
    value: kpis[kpi]?.value ?? null,
    status: kpis[kpi]?.status ?? 'neutral',
  }));
  // Meta: sincronização real (com erro, atrasada ou sem leitura = instável); nunca o status simulado.
  const metaPlatformStatus: TrackingHealth['metaPlatformStatus'] =
    !m || metaGap || metaFreshness(now, m, r).some((f) => f.stale) ? 'degraded' : 'ok';
  const overall: TrackingHealth['overall'] = indicators.some((i) => i.status === 'bad')
    ? 'critical'
    : indicators.some((i) => i.status === 'warn') || metaPlatformStatus !== 'ok'
      ? 'attention'
      : 'healthy';
  const tail = front.length
    ? ` · ${pct0((front.length - noOrigin) / front.length)} das vendas com origem`
    : '';
  const sentence =
    overall === 'healthy'
      ? `Rastreamento saudável${tail}`
      : overall === 'attention'
        ? `Rastreamento pede atenção${tail}`
        : `Rastreamento crítico${tail}`;
  return { health: { overall, sentence, indicators, metaPlatformStatus, live: true, notes }, kpis };
}

/** Evento do log ao vivo: a hora de chegada do aviso ainda não vem do servidor (latência "—"). */
export type LiveEventRow = TrackedEventRow & { latencyKnown: false };

/**
 * Log de eventos a partir dos pedidos reais da Kiwify (sem dado pessoal): Pix e boleto gerados,
 * aprovação, recusa, carrinho abandonado, reembolso e chargeback, cada um na hora do próprio evento
 * dentro do período. O servidor ainda não manda a hora de chegada de cada aviso: a hora mostrada é a
 * do evento e a latência fica "—". Eventos do tracker (quiz, aula, botão) não existem sem ele.
 */
export function liveEvents(
  rows: readonly RealOrderRow[],
  refunds: readonly RealOrderRow[] | null,
  r: { from: number; to: number },
): LiveEventRow[] {
  const out: LiveEventRow[] = [];
  const add = (o: RealOrderRow, type: FunnelEventType, at: number) => {
    if (at < r.from || at >= r.to) return;
    out.push({
      id: `${o.id}:${type}`,
      type,
      source: 'kiwify',
      occurredAt: at,
      receivedAt: at,
      latencyMs: 0,
      latencyKnown: false,
      dedupKey: `${o.ref ?? o.id}:${type}`,
      status: 'saved',
      ...(o.sck !== undefined ? { sck: o.sck || null } : {}),
    });
  };
  const refundIds = new Set((refunds ?? []).map((o) => o.id));
  for (const o of rows) {
    const d = orderDates(o);
    const pay = (o.payment ?? '').toLowerCase();
    const refundLike = isRefundLike(o.status);
    if (o.rawStatus === 'abandoned') add(o, 'cart.abandoned', o.at);
    if (isPaid(o) || o.status === 'waiting_payment' || refundLike) {
      if (pay === 'pix') add(o, 'order.pix_created', d.createdAt);
      else if (pay.includes('boleto')) add(o, 'order.boleto_created', d.createdAt);
    }
    if (d.paidAt != null) add(o, 'order.approved', d.paidAt);
    if (o.status === 'refused') add(o, 'order.refused', o.at);
    // Reembolso pela data do reembolso (lista própria do servidor; servidor antigo: pela compra).
    if (refundLike && !refunds) add(o, o.status === 'refunded' ? 'order.refunded' : 'order.chargeback', o.at);
    else if (refundLike && !refundIds.has(o.id) && d.refundedAt != null)
      add(o, o.status === 'refunded' ? 'order.refunded' : 'order.chargeback', d.refundedAt);
  }
  for (const o of refunds ?? [])
    if (isRefundLike(o.status))
      add(o, o.status === 'refunded' ? 'order.refunded' : 'order.chargeback', o.refundedAt ?? o.at);
  return out.sort((a, b) => b.receivedAt - a.receivedAt || a.id.localeCompare(b.id));
}

/** Página da lista sem medição (o diagnóstico de UTMs por anúncio): vazia e marcada, nunca "tudo certo". */
export type UnmeasuredPage = Page<never> & { unmeasured: 'meta_url_tags' };

/** Relatório do quiz vazio: sem tracker, nenhuma visita, resposta ou tempo é medido. */
const EMPTY_QUIZ: QuizReport = {
  questions: [],
  views: 0,
  starts: 0,
  completes: 0,
  toVsl: 0,
  steps: [],
  resultFunnel: { resultViews: 0, ctaClicks: 0, vslLoaded: 0, medianSecToVsl: 0 },
  freshness: [],
};

const notReady = (message: string, code: string) => Object.assign(new Error(message), { code });

export function funnelModule(ctx: RealCtx, reports: RealModule = reportsModule(ctx)): RealModule {
  const { clock, v2 } = ctx;

  function freshnessOf(now: number, m: RealMedia | null, v: RealVsl | null, withMedia: boolean) {
    const out: SuiteFreshness[] = [...(ctx.kiwiFresh(now) as SuiteFreshness[])];
    if (m && withMedia) out.push(...(ctx.metaFresh(now, m.lastSyncAt) as SuiteFreshness[]));
    if (v?.configured && v.main) {
      out.push({
        source: 'vturb',
        latency: 'near_realtime',
        fetchedAt: now,
        dataAsOf: v.generatedAt,
        stale: false,
      });
    }
    return out;
  }

  async function getFunnelV2(q: Parameters<GetFunnelV2>[0]): Promise<LiveFunnelV2Report> {
    const sim = await (v2.getFunnelV2 as GetFunnelV2)(q);
    const now = clock();
    const r = rangeMs(q, now);
    const [rows, m, v] = await Promise.all([
      ctx.load(r.from, r.to).catch(() => [] as RealOrderRow[]),
      ctx.loadMedia(r.from, r.to),
      ctx.loadVsl(r.from, r.to),
    ]);
    const view = q.channel ?? 'all';
    const all = rows.filter((o) => isPaid(o) && matches(o, q));
    // Contagem de vendas: as aprovadas, inclusive as reembolsadas depois (a regra da tela Vendas e
    // do Hoje); comissão, coorte e ramos seguem com as pagas.
    const sold = rows.filter((o) => isSold(o) && matches(o, q));
    const soldView = sold.filter((o) => inChannelView(o, view));
    const vm = v?.configured && !v.error && view === 'all' ? v.main : null;
    const vs = vm ? vslStageCounts(vm) : null;
    const media = m && view !== 'organic' ? m : null;
    // Etapa medida de verdade fica com o número real; a que não é medida (quiz, botão, consultorias) fica vazia.
    const real: Partial<Record<FunnelStageIdV2, { count: number | null; source: FunnelStageV2['source'] }>> =
      {
        impressions: { count: media ? media.totals.impressions : null, source: 'meta' },
        clicks: { count: media ? media.totals.linkClicks : null, source: 'meta' },
        quiz_start: { count: null, source: 'tracker' },
        quiz_complete: { count: null, source: 'tracker' },
        vsl_visit: { count: vs ? vs.views : null, source: 'vturb' },
        vsl_play: { count: vs ? vs.plays : null, source: 'vturb' },
        // Minuto 1 na base do play (aparelhos): a fração da curva aplicada aos plays.
        minute1: { count: vs ? vs.minute1 : null, source: 'vturb' },
        pitch: { count: vs ? vs.pitch : null, source: 'vturb' },
        cta_click: { count: null, source: 'tracker' },
        checkout: { count: null, source: 'kiwify' },
        // Curso e 20X pelo canal da venda na Kiwify (no Anúncios, pelo link/UTM: a tela diz isso).
        front: { count: soldView.filter((o) => o.kind === 'front').length, source: 'kiwify' },
        // Só o 20X: a consulta Premium (e produto desconhecido) não é degrau do 20X.
        upsell: { count: soldView.filter((o) => o.kind === 'upsell').length, source: 'kiwify' },
      };
    const stages = sim.stages.map((st): FunnelStageV2 => {
      const x = real[st.id];
      return x ? { ...st, count: x.count, source: x.source, estimated: false } : { ...st, count: null };
    });
    const notMeasured: LiveFunnelExtras['notMeasured'] = {};
    for (const st of stages) if (st.count == null) notMeasured[st.id] = reasonOf(st.id, view, v);
    // Meta com erro, não conectada ou hoje sem leitura: o gasto dos Anúncios fica sem valor.
    const ch = liveChannelRows(all, mediaMissing(ctx.loadMedia, m, now, r) == null ? m : null);
    return {
      ...sim,
      view,
      // O recorte pedido (a simulação pode ter outro "hoje").
      from: q.range.from,
      to: q.range.to,
      stages,
      // Consultorias em negociação não são registradas ainda: nada da simulação.
      backendNegotiating: undefined,
      // Ramos, quiz, perfil e medição: sem tracker instalado, só o que os pedidos reais mostram.
      ...liveCohort(all, q.range.from),
      // Mesmo recorte do `getChannels` (o FUN-07 pede o Todos e filtra o canal na tela).
      channels: ch as FunnelV2Report['channels'],
      freshness: freshnessOf(now, m, vm ? v : null, view !== 'organic'),
      live: true,
      adsMeta: view !== 'organic' && m?.meta ? { ...m.meta } : null,
      notMeasured,
      backendMeasured: false,
      noOrigin: sold.filter((o) => o.kind === 'front' && o.channel === 'sem_origem').length,
      // Play, minuto 1 e pitch que não fecham (a VTurb mandou bases diferentes): a tela avisa.
      ...(vs?.outOfOrder ? { vslOutOfOrder: true } : {}),
      // VTurb ligada que não respondeu: as etapas da VSL ficam "Não medido" com o motivo "não
      // respondeu" (não "não conectada"). O motivo `vturb_off` fica, com esta marca ao lado, até o
      // tipo `NotMeasuredReason` (realFunnelTypes.ts) ganhar o `vturb_error`.
      ...(v?.configured && v.error ? { vturbError: true } : {}),
    };
  }

  async function getChannels(q: Query): Promise<ChannelReport> {
    const now = clock();
    const r = rangeMs(q, now);
    const [rows, m] = await Promise.all([ctx.load(r.from, r.to), ctx.loadMedia(r.from, r.to)]);
    const ch = liveChannelRows(
      rows.filter((o) => isPaid(o) && matches(o, q)),
      mediaMissing(ctx.loadMedia, m, now, r) == null ? m : null,
    );
    return {
      rows: ch.rows as ChannelRow[],
      posts: [],
      coverage: ch.coverage,
      freshness: freshnessOf(now, m, null, true),
    };
  }

  /**
   * Funil da V1 ao vivo: só as etapas com fonte real (Meta, VTurb e Kiwify), sem comparação nem
   * julgamento, e a cascata do CPA vazia (ainda não é calculada com dados reais). Quiz, botão,
   * checkout, upsell visto e área de membros saem da lista.
   */
  async function getFunnel(q: Query): Promise<FunnelReport> {
    const now = clock();
    const r = rangeMs(q, now);
    const [rows, m, v] = await Promise.all([
      ctx.load(r.from, r.to).catch(() => [] as RealOrderRow[]),
      ctx.loadMedia(r.from, r.to),
      ctx.loadVsl(r.from, r.to),
    ]);
    const paid = rows.filter((o) => isPaid(o) && matches(o, q));
    const vm = v?.configured ? v.main : null;
    const value: Partial<Record<FunnelStepId, number | null>> = {
      impressions: m ? m.totals.impressions : null,
      linkClicks: m ? m.totals.linkClicks : null,
      lpv: m ? m.totals.lpv : null,
      vslView: vm ? vm.stats.views : null,
      vslPlay: vm ? vm.stats.plays : null,
      pitch: vm ? vm.stats.overPitch : null,
      frontSale: paid.filter((o) => o.kind === 'front').length,
      upsellSale: paid.filter((o) => o.kind === 'upsell').length,
    };
    const steps: FunnelStep[] = [];
    let top: number | null = null;
    let prev: number | null = null;
    for (const s of V1_STEPS) {
      const n = value[s.id];
      if (n == null) continue;
      top ??= n;
      steps.push({
        id: s.id,
        label: s.label,
        source: s.source,
        value: n,
        compareValue: null,
        rateFromPrev: prev != null && prev > 0 ? n / prev : null,
        rateFromTop: top > 0 ? n / top : null,
        ...(s.rateKpi ? { rateKpi: s.rateKpi } : {}),
        status: 'neutral',
        deviationVsBaseline: null,
        worst: false,
      });
      prev = n;
    }
    const empty = <K extends string>(keys: readonly K[]) =>
      Object.fromEntries(keys.map((k) => [k, {}])) as Record<K, Partial<Record<KpiId, number | null>>>;
    return {
      steps,
      cascade: { cpaNow: null, cpaBaseline: null, factors: [], probableCause: null },
      byDevice: empty(DEVICES),
      byPlacement: empty(PLACEMENTS),
    };
  }

  async function listUntracked(q: ListQuery): Promise<Page<unknown>> {
    const now = clock();
    const r = rangeMs(q, now);
    // Os mesmos filtros do semáforo (TRK-01): a contagem e a lista batem.
    const rows = (await ctx.load(r.from, r.to).catch(() => [] as RealOrderRow[])).filter(
      (o) => isPaid(o) && o.kind === 'front' && o.channel === 'sem_origem' && matches(o, q),
    );
    return paginate(
      rows
        .sort((a, b) => b.at - a.at)
        .map((o) => ({
          orderId: o.id,
          at: o.at,
          product: 'front' as const,
          value: revenueOf(o),
          reason: 'utm_empty' as const,
          tracking: {
            utm_source: o.utmSource,
            utm_content: o.utmContent,
            src: o.lastTouch ? `ultimo_${o.lastTouch}` : null,
          },
        })),
      q,
    );
  }

  /** Semáforo real do período (filtros da barra), com a Meta para a divergência. */
  async function trackingOf(q: Query) {
    const now = clock();
    const r = rangeMs(q, now);
    const [rows, m] = await Promise.all([ctx.load(r.from, r.to), ctx.loadMedia(r.from, r.to)]);
    const paid = rows.filter((o) => isPaid(o) && matches(o, q));
    return liveTrackingHealth(
      paid,
      m,
      mediaMissing(ctx.loadMedia, m, now, r),
      now,
      r,
      ctx.kiwiFresh(now),
      ctx.webhookSince?.() ?? null,
    );
  }

  async function getTrackingHealth(q: Query): Promise<TrackingHealth> {
    try {
      return (await trackingOf(q)).health;
    } catch {
      // Sem os pedidos reais, erro da fonte: nunca o semáforo da simulação.
      throw notReady(
        'Kiwify sem resposta: o semáforo volta quando os pedidos carregarem',
        'source_unavailable',
      );
    }
  }

  /**
   * KPIs sem fonte real (tracker; VSL sem a VTurb) ficam sem valor com o motivo, e os do semáforo saem
   * dos pedidos reais: o snapshot, o detalhe ?m= e o stream mostram o mesmo número que o TRK-01.
   */
  async function kpis({ q, now, r }: RealKpiInput): Promise<Partial<Record<KpiId, KpiValue>>> {
    const out: Partial<Record<KpiId, KpiValue>> = {};
    for (const id of TRACKER_KPIS) out[id] = noRealKpi(id, NO_TRACKER);
    const [rows, m, v] = await Promise.all([
      ctx.load(r.from, r.to).catch(() => null),
      ctx.loadMedia(r.from, r.to),
      ctx.loadVsl(r.from, r.to),
    ]);
    if (!(v?.configured && v.main)) for (const id of VSL_RATE_KPIS) out[id] = noRealKpi(id, NO_VTURB);
    if (rows) {
      const paid = rows.filter((o) => isPaid(o) && matches(o, q));
      const h = liveTrackingHealth(
        paid,
        m,
        mediaMissing(ctx.loadMedia, m, now, r),
        now,
        r,
        ctx.kiwiFresh(now),
        ctx.webhookSince?.() ?? null,
      );
      Object.assign(out, h.kpis);
    } else {
      // Sem os pedidos: todo indicador do semáforo fica sem valor (o do snapshot simulado nunca passa).
      for (const id of TRACKING_KPIS) out[id] = noRealKpi(id, NO_KIWIFY_ORDERS);
    }
    return out;
  }

  /** Séries sem fonte real por hora/dia (taxas do FUN-04, KPIs do tracker): eixo vazio, nunca a simulação. */
  const NO_SERIES: ReadonlySet<KpiId> = new Set<KpiId>([...TRACKER_KPIS, ...TRACKING_KPIS]);
  async function series(q: SeriesQuery): Promise<ColumnarSeries | undefined> {
    const byKpi = (q.key === 'kpiByHour' || q.key === 'kpiByDay') && q.kpi != null && NO_SERIES.has(q.kpi);
    // As taxas por hora do FUN-04 (CTR inclusive) não têm série real: a Meta só manda o gasto por hora.
    if (q.key !== 'funnelRateByHour' && !byKpi) return undefined;
    const now = clock();
    const r = rangeMs(q, now);
    const size = q.key === 'kpiByDay' || q.bucket === '1d' ? DAY : HOUR;
    return { t: buckets([], r.from, r.end, size, now, () => 0).t, series: [], markers: [], freshness: [] };
  }

  async function listEvents(q: ListQuery): Promise<Page<unknown>> {
    const now = clock();
    const r = rangeMs(q, now);
    const [rows, refunds] = await Promise.all([
      ctx.load(r.from, r.to),
      ctx.loadRefunds ? ctx.loadRefunds(r.from, r.to) : Promise.resolve(null),
    ]);
    let list = liveEvents(
      rows.filter((o) => matches(o, q)),
      refunds?.filter((o) => matches(o, q)) ?? null,
      r,
    );
    // `types` é OU: tipo do evento ou a fonte (como o provider da demonstração).
    if (q.types?.length) list = list.filter((e) => q.types?.includes(e.type) || q.types?.includes(e.source));
    if (q.status?.length) list = list.filter((e) => q.status?.includes(e.status));
    return paginate(list, q);
  }

  const unmeasuredUtms: UnmeasuredPage = {
    rows: [],
    nextCursor: null,
    total: 0,
    unmeasured: 'meta_url_tags',
  };

  return {
    kpis,
    kpiIds: [...TRACKER_KPIS, ...TRACKING_KPIS, ...VSL_RATE_KPIS],
    // Do stream simulado nada disso passa: sem número real, o cartão fica sem valor.
    neverSimKpis: [...TRACKER_KPIS, ...TRACKING_KPIS, ...VSL_RATE_KPIS],
    series,
    // O nome e a ajuda dos indicadores do semáforo dizem a conta real (UTM e sem origem).
    patchBoot: async (boot) => withLiveTrackingTexts(boot),
    async patchSnapshot(q, snap) {
      // O semáforo do snapshot (HOJ-11, Hoje) é o mesmo do TRK-01.
      snap.tracking = await getTrackingHealth(q).catch(
        (): TrackingHealth => ({
          overall: 'attention',
          sentence: 'Rastreamento sem leitura: os pedidos da Kiwify não carregaram',
          indicators: TRACKING_KPIS.map((kpi) => ({ kpi, value: null, status: 'neutral' })),
          metaPlatformStatus: 'degraded',
        }),
      );
      if (snap.kpisV2)
        for (const id of TRACKER_KPIS_V2)
          if (snap.kpisV2[id])
            snap.kpisV2[id] = noRealKpi(id, id === 'trackedClicks' ? NO_REDIRECTOR : NO_TRACKER);
    },
    patchKpisV2(q, out, sim) {
      // Só o que ninguém trocou (outro módulo com número real vence).
      for (const id of TRACKER_KPIS_V2)
        if (q.ids.includes(id) && out[id] === sim[id])
          out[id] = noRealKpi(id, id === 'trackedClicks' ? NO_REDIRECTOR : NO_TRACKER);
    },
    overrides: {
      // Relatórios (briefings) e objeções da Pauta vêm juntos com o funil (o resumo do dia e o que o
      // expert grava): o `realSales.ts` não precisa de um módulo a mais para eles.
      ...reports.overrides,
      getFunnel,
      getTrackingHealth,
      // Sem tracker: nenhuma visita, resposta, perfil ou segmento do quiz é medido (nada simulado).
      getQuiz: async () => EMPTY_QUIZ,
      getSegments: async (): Promise<SegmentReport> => ({ cells: [], buyerOfTheDay: null, angles: [] }),
      ...(v2.getFunnelV2 ? { getFunnelV2 } : {}),
      ...(v2.getChannels ? { getChannels } : {}),
      // Redirecionador ainda não existe no servidor: nenhum link curto, e criar um é recusado.
      ...(v2.listTrackedLinks ? { listTrackedLinks: async (): Promise<TrackedLink[]> => [] } : {}),
      ...(v2.upsertTrackedLink
        ? {
            upsertTrackedLink: async (): Promise<TrackedLink> => {
              throw notReady(
                'Redirecionador não configurado: o painel ainda não cria links curtos',
                'not_configured',
              );
            },
          }
        : {}),
    },
    list: (kind, q) => {
      const fromReports = reports.list?.(kind, q);
      if (fromReports) return fromReports;
      if (kind === 'untracked') return listUntracked(q);
      if (kind === 'events') return listEvents(q);
      // A Meta ainda não manda os parâmetros de URL dos anúncios: o diagnóstico não é medido.
      if (kind === 'utmDiagnostics') return Promise.resolve(unmeasuredUtms);
      return undefined;
    },
  };
}
